import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { useTheme } from "../context/ThemeContext";
import { Scroll, Moon, Sun, BookMarked, ScrollText } from "lucide-react";

const SIZES = [
  { key: "s", name: "ק" },
  { key: "m", name: "מ" },
  { key: "l", name: "ג" },
  { key: "xl", name: "ע" },
  { key: "2xl", name: "ל" },
];

export default function ReaderSettings({ open, onClose }) {
  const { theme, fontSize, showVerseNums, focusMode, readingMode, autoContinue, update } = useTheme();

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="left"
        dir="rtl"
        className="w-full sm:max-w-md overflow-y-auto bg-background border-l border-gold/30"
        data-testid="reader-settings"
      >
        <SheetHeader className="text-right">
          <SheetTitle className="font-heading text-2xl text-foreground">
            הגדרות קריאה
          </SheetTitle>
          <div className="ornamental-divider">✦</div>
        </SheetHeader>

        <div className="mt-6 space-y-8">
          {/* Reading mode */}
          <section className="space-y-3">
            <Label className="font-heading text-base">מצב קריאה</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                data-testid="settings-mode-book"
                onClick={() => {
                  update({ readingMode: "book" });
                  sessionStorage.removeItem("tanakh_book_opened");
                }}
                className={`flex flex-col items-center gap-2 rounded-sm border p-4 transition-all ${
                  readingMode === "book"
                    ? "border-gold bg-gold/10 text-foreground"
                    : "border-border/60 text-foreground/70 hover:border-gold/40"
                }`}
              >
                <BookMarked className="h-5 w-5" strokeWidth={1.5} />
                <span className="font-heading text-sm">מצב ספר 3D</span>
                <span className="text-xs text-muted-foreground">דפי ספר אמיתיים</span>
              </button>
              <button
                data-testid="settings-mode-study"
                onClick={() => update({ readingMode: "study" })}
                className={`flex flex-col items-center gap-2 rounded-sm border p-4 transition-all ${
                  readingMode === "study"
                    ? "border-gold bg-gold/10 text-foreground"
                    : "border-border/60 text-foreground/70 hover:border-gold/40"
                }`}
              >
                <ScrollText className="h-5 w-5" strokeWidth={1.5} />
                <span className="font-heading text-sm">מצב לימוד</span>
                <span className="text-xs text-muted-foreground">קריאה שטוחה ומהירה</span>
              </button>
            </div>
          </section>

          {/* Theme */}
          <section className="space-y-3">
            <Label className="font-heading text-base">ערכת נושא</Label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { k: "parchment", he: "קלף", Icon: Scroll },
                { k: "dark", he: "לילה", Icon: Moon },
                { k: "white", he: "לבן", Icon: Sun },
              ].map((t) => (
                <button
                  key={t.k}
                  data-testid={`settings-theme-${t.k}`}
                  onClick={() => update({ theme: t.k })}
                  className={`flex flex-col items-center gap-2 rounded-sm border p-3 transition-all ${
                    theme === t.k
                      ? "border-gold bg-gold/10 text-foreground"
                      : "border-border/60 text-foreground/70 hover:border-gold/40"
                  }`}
                >
                  <t.Icon className="h-5 w-5" strokeWidth={1.5} />
                  <span className="font-heading text-sm">{t.he}</span>
                </button>
              ))}
            </div>
          </section>

          {/* Font size */}
          <section className="space-y-3">
            <Label className="font-heading text-base">גודל גופן</Label>
            <div className="flex gap-2">
              {SIZES.map((s) => (
                <button
                  key={s.key}
                  data-testid={`settings-size-${s.key}`}
                  onClick={() => update({ fontSize: s.key })}
                  className={`flex-1 rounded-sm border py-2.5 font-heading transition-all ${
                    fontSize === s.key
                      ? "border-gold bg-gold/10 text-foreground"
                      : "border-border/60 text-foreground/70 hover:border-gold/40"
                  }`}
                  style={{
                    fontSize:
                      s.key === "s"
                        ? "14px"
                        : s.key === "m"
                          ? "18px"
                          : s.key === "l"
                            ? "22px"
                            : s.key === "xl"
                              ? "26px"
                              : "30px",
                  }}
                >
                  {s.name}
                </button>
              ))}
            </div>
          </section>

          {/* Toggles */}
          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="nums" className="font-heading text-base">מספרי פסוקים</Label>
                <p className="text-xs text-muted-foreground">הצגת מספר פסוק בתחילת כל פסוק</p>
              </div>
              <Switch
                id="nums"
                data-testid="settings-verse-nums"
                checked={showVerseNums}
                onCheckedChange={(v) => update({ showVerseNums: v })}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="focus" className="font-heading text-base">מצב קריאה מלא</Label>
                <p className="text-xs text-muted-foreground">הסתרת סרגלים לקריאה ללא הפרעות</p>
              </div>
              <Switch
                id="focus"
                data-testid="settings-focus"
                checked={focusMode}
                onCheckedChange={(v) => update({ focusMode: v })}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="auto-continue" className="font-heading text-base">קריאה רציפה</Label>
                <p className="text-xs text-muted-foreground">מעבר אוטומטי לפרק הבא בסיום הפרק</p>
              </div>
              <Switch
                id="auto-continue"
                data-testid="settings-auto-continue"
                checked={!!autoContinue}
                onCheckedChange={(v) => update({ autoContinue: v })}
              />
            </div>
          </section>

          <div className="gold-rule" />
          <p className="text-xs text-muted-foreground text-center">
            כל ההעדפות נשמרות באופן אוטומטי.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
